/**
 * RelayTeamsMap
 *
 * Leaflet map showing all relay teams on a world route simultaneously.
 * Each team is rendered as a coloured progress polyline + animated dot at
 * their current position.  A checkbox strip below the map lets users
 * show / hide individual teams.
 *
 * Props:
 *   teams            – Array of leaderboard/journey rows:
 *                        { team_id, team_name, team_logo?, effective_distance (metres),
 *                          completion_percent, current_city?, is_finisher_eligible?,
 *                          is_frozen?, rank? }
 *   route            – Array of { name, emoji, cumulative_km } — ordered start→finish.
 *                      Falls back to DEFAULT_WORLD_ROUTE if omitted.
 *   checkpoints      – Array of normalised BE checkpoint objects:
 *                        { name, lat, lng, distanceKm, emoji? }
 *                      When provided, lat/lng values override the hardcoded CITY_COORDS table
 *                      for matching city names.  Resolved via resolveCheckpoints() +
 *                      normalizeCheckpoint() in the parent (same pattern as CampaignRelayMap).
 *   targetKm         – Total relay distance in km (default 15 000).
 *   height           – Map canvas height in px (default 380).
 *   provider         – Leaflet tile-layer config (default: OSM from mapProviderConfig).
 *
 *   ── Controlled mode (pass both to override internal state) ──
 *   visible          – Set<team_id>  which teams to show
 *   onToggle         – (team_id) => void  called when user clicks a checkbox
 *   highlightedTeamId– team_id to emphasise (thicker line, others dimmed)
 */
import { useEffect, useRef, useState, useMemo } from 'react';
import L from 'leaflet';
import { defaultMapProvider } from './mapProviderConfig';

// ── Mirrors service-world-expedition DEFAULT_RELAY_ROUTE ──────────────────────
export const DEFAULT_WORLD_ROUTE = [
  { name: 'Hà Nội',   emoji: '🇻🇳', cumulative_km: 0     },
  { name: 'Bangkok',  emoji: '🇹🇭', cumulative_km: 1500  },
  { name: 'Dubai',    emoji: '🇦🇪', cumulative_km: 5500  },
  { name: 'Rome',     emoji: '🇮🇹', cumulative_km: 8500  },
  { name: 'Paris',    emoji: '🇫🇷', cumulative_km: 9500  },
  { name: 'New York', emoji: '🇺🇸', cumulative_km: 14500 },
  { name: 'Đích',     emoji: '🏁',  cumulative_km: 15000 },
];

// Lat/lng for each known city name — extend as needed for custom routes
const CITY_COORDS = {
  'Hà Nội':   [21.0285, 105.8542],
  'Bangkok':  [13.7563, 100.5018],
  'Dubai':    [25.2048,  55.2708],
  'Rome':     [41.9028,  12.4964],
  'Paris':    [48.8566,   2.3522],
  'New York': [40.7128,  -74.006],
  'Đích':     [40.7128,  -74.006],
  // Vietnam domestic route
  'Lũng Cú':   [23.3751, 105.3280],
  'Hà Giang':  [22.8231, 104.9833],
  'Điện Biên': [21.3860, 103.0230],
  'Hà Nội':    [21.0285, 105.8542],
  'Đà Nẵng':   [16.0471, 108.2068],
  'Hội An':    [15.8801, 108.3380],
  'Đà Lạt':   [11.9404, 108.4583],
  'TP.HCM':   [10.8231, 106.6297],
  'Cà Mau':   [ 9.1769, 105.1524],
};

// 10 distinct team colours
const TEAM_COLORS = [
  '#1677ff', '#52c41a', '#fa8c16', '#722ed1',
  '#eb2f96', '#13c2c2', '#faad14', '#2f54eb',
  '#f5222d', '#a0d911',
];

export function getTeamColor(index) {
  return TEAM_COLORS[index % TEAM_COLORS.length];
}

// ── Geometry helpers ──────────────────────────────────────────────────────────

/**
 * Resolve [lat, lng] for a route node.
 * Priority: inline lat/lng on the node (from BE checkpoints) → CITY_COORDS fallback.
 */
function getNodeCoords(node) {
  if (node.lat != null && node.lng != null) return [node.lat, node.lng];
  return CITY_COORDS[node.name] || null;
}

/** Interpolate [lat, lng] at exactKm along the route */
function interpolatePosition(route, effectiveKm) {
  if (!route || route.length < 2) return null;
  const last = route[route.length - 1];
  const clampedKm = Math.min(effectiveKm, last.cumulative_km);

  for (let i = 1; i < route.length; i++) {
    const prev = route[i - 1];
    const curr = route[i];
    if (clampedKm <= curr.cumulative_km) {
      const a = getNodeCoords(prev);
      const b = getNodeCoords(curr);
      if (!a || !b) return null;
      const segLen = curr.cumulative_km - prev.cumulative_km;
      const t = segLen > 0 ? (clampedKm - prev.cumulative_km) / segLen : 0;
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
  }
  return getNodeCoords(last) || null;
}

/** Return polyline points from route start → effectiveKm */
function completedPath(route, effectiveKm) {
  const pts = [];
  for (let i = 0; i < route.length; i++) {
    const city = route[i];
    const coords = getNodeCoords(city);
    if (!coords) continue;
    if (city.cumulative_km <= effectiveKm) {
      pts.push(coords);
    } else {
      if (i > 0) {
        const ip = interpolatePosition(route, effectiveKm);
        if (ip) pts.push(ip);
      }
      break;
    }
  }
  return pts;
}

// ── Marker HTML builders ──────────────────────────────────────────────────────

function cityMarkerHtml(emoji) {
  return `<div style="
    width:28px;height:28px;
    border-radius:50%;
    background:#fff;
    border:2px solid #d9d9d9;
    display:flex;align-items:center;justify-content:center;
    font-size:14px;
    box-shadow:0 2px 8px rgba(0,0,0,.14);
  ">${emoji}</div>`;
}

function teamDotHtml(color, isFrozen, isFront) {
  const size  = isFront ? 16 : 12;
  const pulse = isFront
    ? `box-shadow:0 0 0 4px ${color}44,0 2px 8px rgba(0,0,0,.2);`
    : 'box-shadow:0 1px 4px rgba(0,0,0,.18);';
  const border = isFrozen ? '2px dashed #8c8c8c' : `2px solid #fff`;
  return `<div style="
    width:${size}px;height:${size}px;
    border-radius:50%;
    background:${color};
    border:${border};
    ${pulse}
  "></div>`;
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function RelayTeamsMap({
  teams            = [],
  route,
  checkpoints      = null,   // Array<{name, lat, lng, distanceKm, emoji?}> from BE campaign
  targetKm         = 15000,
  height           = 380,
  provider         = defaultMapProvider,
  // ── controlled props (optional) ──
  visible:         visibleProp,
  onToggle:        onToggleProp,
  highlightedTeamId = null,
}) {
  // Build base route from prop or default
  const baseRoute = route && route.length >= 2 ? route : DEFAULT_WORLD_ROUTE;

  /**
   * Enrich route nodes with lat/lng from BE checkpoints (matched by name).
   * Checkpoint data takes priority over the hardcoded CITY_COORDS table.
   * This is recomputed only when the checkpoints or route changes.
   */
  const checkpointKey = checkpoints
    ? checkpoints.map(c => `${c.name}:${c.lat}:${c.lng}`).join('|')
    : '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resolvedRoute = useMemo(() => {
    if (!checkpoints || checkpoints.length === 0) return baseRoute;
    // Build a name → {lat, lng} index from BE checkpoints
    const cpIndex = {};
    checkpoints.forEach(cp => {
      if (cp.name && cp.lat != null && cp.lng != null) {
        cpIndex[cp.name] = { lat: cp.lat, lng: cp.lng };
      }
    });
    return baseRoute.map(node => {
      const cp = cpIndex[node.name];
      return cp ? { ...node, lat: cp.lat, lng: cp.lng } : node;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpointKey, baseRoute]);

  const containerRef = useRef(null);
  const mapRef       = useRef(null);

  // ── Uncontrolled internal state (used when caller doesn't pass visible/onToggle) ──
  const [internalVisible, setInternalVisible] = useState(
    () => new Set(teams.map(t => t.team_id))
  );
  const teamIds = teams.map(t => t.team_id).join(',');
  useEffect(() => {
    if (!visibleProp) setInternalVisible(new Set(teams.map(t => t.team_id)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamIds]);

  // Resolve controlled vs uncontrolled
  const visible    = visibleProp ?? internalVisible;
  const toggleTeam = onToggleProp ?? ((id) =>
    setInternalVisible(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    })
  );

  const selectAll  = () => {
    if (onToggleProp) { teams.forEach(t => { if (!visibleProp.has(t.team_id)) onToggleProp(t.team_id); }); }
    else setInternalVisible(new Set(teams.map(t => t.team_id)));
  };
  const selectNone = () => {
    if (onToggleProp) { teams.forEach(t => { if (visibleProp.has(t.team_id)) onToggleProp(t.team_id); }); }
    else setInternalVisible(new Set());
  };

  // Full route points (for background polyline + fitBounds)
  // Uses getNodeCoords so BE-enriched lat/lng takes priority over CITY_COORDS.
  const fullRoutePoints = useMemo(
    () => resolvedRoute.map(c => getNodeCoords(c)).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolvedRoute.map(c => `${c.name}:${c.lat ?? ''}:${c.lng ?? ''}`).join(',')]
  );

  // ── Leaflet effect (re-runs when visible set or teams data changes) ──────────
  useEffect(() => {
    if (!containerRef.current || fullRoutePoints.length < 2) return;

    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });
    mapRef.current = map;

    // Tile layer
    L.tileLayer(provider.tileUrl, {
      attribution: provider.attribution,
      maxZoom: provider.maxZoom,
    }).addTo(map);

    // ── Background route (dashed grey) ────────────────────────────────────────
    const bgLine = L.polyline(fullRoutePoints, {
      color: '#d9d9d9',
      weight: 2.5,
      dashArray: '7 9',
      opacity: 0.7,
    }).addTo(map);

    // ── City checkpoint markers ───────────────────────────────────────────────
    resolvedRoute.forEach((city) => {
      const coords = getNodeCoords(city);
      if (!coords) return;
      L.marker(coords, {
        icon: L.divIcon({
          className: '',
          html: cityMarkerHtml(city.emoji),
          iconAnchor: [14, 14],
        }),
        interactive: true,
      })
        .bindTooltip(
          `<strong>${city.name}</strong><br>${city.cumulative_km.toLocaleString()} km`,
          { direction: 'top', offset: [0, -18] }
        )
        .addTo(map);
    });

    // ── Team layers ───────────────────────────────────────────────────────────
    const hasHighlight = highlightedTeamId != null;

    teams.forEach((team, idx) => {
      if (!visible.has(team.team_id)) return;

      const color       = getTeamColor(idx);
      const effKm       = (team.effective_distance || 0) / 1000;
      const path        = completedPath(resolvedRoute, effKm);
      const currentPos  = interpolatePosition(resolvedRoute, effKm);
      const isFrozen    = Boolean(team.is_frozen);
      const isHighlight = team.team_id === highlightedTeamId;

      // Dimmed when another team is highlighted
      const opacity = hasHighlight ? (isHighlight ? 1 : 0.22) : 0.88;
      const weight  = isHighlight ? 5.5 : 3.5;

      // Completed polyline
      if (path.length >= 2) {
        L.polyline(path, {
          color,
          weight,
          opacity,
          ...(isFrozen ? { dashArray: '8 6' } : {}),
        }).addTo(map);
      }

      // Current position dot
      if (currentPos) {
        const isFront = isHighlight || idx === 0;
        L.marker(currentPos, {
          icon: L.divIcon({
            className: '',
            html: teamDotHtml(color, isFrozen, isFront),
            iconAnchor: [isFront ? 8 : 6, isFront ? 8 : 6],
          }),
          zIndexOffset: isHighlight ? 2000 : 1000 - idx * 10,
          opacity,
        })
          .bindTooltip(
            `<strong style="color:${color}">${team.team_name}</strong><br>` +
            `${effKm.toFixed(1)} km · ${team.completion_percent?.toFixed(1)}%` +
            (isFrozen ? '<br><em>🔒 Đang bị freeze</em>' : ''),
            { direction: 'top', offset: [0, -12], permanent: isHighlight }
          )
          .addTo(map);
      }
    });

    // Fit to the full route extent
    map.fitBounds(bgLine.getBounds(), {
      padding: [36, 36],
      maxZoom: 5,
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  // We intentionally stringify visible/teams/checkpoints to detect set/array changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullRoutePoints, teams, [...visible].sort().join(','), highlightedTeamId, provider, checkpointKey]);

  if (fullRoutePoints.length < 2) return null;

  return (
    <div>
      {/* ── Map canvas ────────────────────────────────────────────────────── */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height,
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid #f0f0f0',
          boxShadow: '0 2px 12px rgba(0,0,0,.06)',
        }}
      />

      {/* ── Team toggle strip ─────────────────────────────────────────────── */}
      {teams.length > 0 && (
        <div
          style={{
            marginTop: 10,
            padding: '8px 4px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px 14px',
          }}
        >
          {/* Select-all / none */}
          <div style={{ display: 'flex', gap: 6, marginRight: 6 }}>
            <button
              type="button"
              onClick={selectAll}
              style={ctrlBtn}
            >
              Tất cả
            </button>
            <button
              type="button"
              onClick={selectNone}
              style={ctrlBtn}
            >
              Bỏ chọn
            </button>
          </div>

          {/* Per-team checkboxes */}
          {teams.map((team, idx) => {
            const color   = getTeamColor(idx);
            const checked = visible.has(team.team_id);
            const effKm   = ((team.effective_distance || 0) / 1000).toFixed(1);
            return (
              <label
                key={team.team_id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  cursor: 'pointer',
                  fontSize: 12,
                  userSelect: 'none',
                  opacity: checked ? 1 : 0.4,
                  transition: 'opacity 0.15s',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleTeam(team.team_id)}
                  style={{ accentColor: color, width: 13, height: 13, cursor: 'pointer' }}
                />
                {/* Colour swatch */}
                <span
                  style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: color, flexShrink: 0,
                    ...(team.is_frozen ? { border: '1px dashed #8c8c8c', background: 'transparent' } : {}),
                  }}
                />
                <span style={{ fontWeight: checked ? 600 : 400, color: '#262626' }}>
                  {team.rank && `#${team.rank} `}{team.team_name}
                </span>
                <span style={{ color: '#8c8c8c' }}>{effKm} km</span>
                {team.is_frozen && <span title="Đang bị freeze">🔒</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ctrlBtn = {
  padding: '2px 10px',
  fontSize: 11,
  border: '1px solid #d9d9d9',
  borderRadius: 6,
  background: '#fafafa',
  cursor: 'pointer',
  color: '#595959',
};
